import { requestJson } from "../http/httpClient";
import { readArray, readNullableString, readObject, readString } from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";
import { readTermCard, type TermCard } from "./ontologyApi";

/**
 * Each term a change set adds, with its mappings to the reference vocabularies the project
 * binds and the evidence for its triggers (`docs/components/checks.md` TA-6). The review
 * puts both definitions of a mapped pair side by side: whether they mean the same is the
 * curator's judgement.
 */

export interface ReferenceConcept {
  readonly vocabulary: string;
  readonly term: TermCard;
}

export interface TermMapping {
  /** `exactMatch`, `closeMatch`, `broadMatch` or `narrowMatch`. */
  readonly relation: string;
  readonly target: string;
  /** `null` if no bound vocabulary declares the target. */
  readonly concept: ReferenceConcept | null;
}

export interface TriggerEvidence {
  readonly property: string;
  readonly form: string;
  readonly language: string | null;
  /** Segment references; empty if none is recorded. */
  readonly attestedIn: readonly string[];
}

export interface TermAlignment {
  readonly term: TermCard;
  readonly mappings: readonly TermMapping[];
  readonly noReferenceMatch: string | null;
  readonly parentDifference: string | null;
  readonly triggers: readonly TriggerEvidence[];
}

export interface ChangeSetAlignments {
  readonly base: string;
  readonly head: string;
  readonly vocabularies: readonly string[];
  readonly problems: readonly string[];
  readonly alignments: readonly TermAlignment[];
}

function readConcept(value: unknown): ReferenceConcept | null {
  if (value === null) {
    return null;
  }
  const concept = readObject(value, "reference concept");
  return { vocabulary: readString(concept, "vocabulary"), term: readTermCard(concept["term"]) };
}

function readMapping(value: unknown): TermMapping {
  const mapping = readObject(value, "term mapping");
  return {
    relation: readString(mapping, "relation"),
    target: readString(mapping, "target"),
    concept: readConcept(mapping["concept"]),
  };
}

function readTrigger(value: unknown): TriggerEvidence {
  const trigger = readObject(value, "trigger evidence");
  return {
    property: readString(trigger, "property"),
    form: readString(trigger, "form"),
    language: readNullableString(trigger, "language"),
    attestedIn: readStringList(trigger, "attested_in"),
  };
}

function readAlignment(value: unknown): TermAlignment {
  const alignment = readObject(value, "term alignment");
  return {
    term: readTermCard(alignment["term"]),
    mappings: readArray(alignment["mappings"], "mappings", readMapping),
    noReferenceMatch: readNullableString(alignment, "no_reference_match"),
    parentDifference: readNullableString(alignment, "parent_difference"),
    triggers: readArray(alignment["triggers"], "triggers", readTrigger),
  };
}

export function readChangeSetAlignments(value: unknown): ChangeSetAlignments {
  const body = readObject(value, "term alignments");
  return {
    base: readString(body, "base"),
    head: readString(body, "head"),
    vocabularies: readStringList(body, "vocabularies"),
    problems: readStringList(body, "problems"),
    alignments: readArray(body["alignments"], "alignments", readAlignment),
  };
}

export function getTermAlignments(changeSetId: string, signal?: AbortSignal): Promise<ChangeSetAlignments> {
  return requestJson({
    method: "GET",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}/term-alignments`,
    signal,
    decodeResponse: readChangeSetAlignments,
  });
}
